import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { Date as DayLabel } from "@dashboard/components/Date/Date";
import { ListPageLayout } from "@dashboard/components/Layouts/List/Root";
import { ResponsiveTable } from "@dashboard/components/ResponsiveTable/ResponsiveTable";
import { TablePaginationWithContext } from "@dashboard/components/TablePagination/TablePaginationWithContext";
import TableRowLink from "@dashboard/components/TableRowLink/TableRowLink";
import { type AtticLotRowFragment } from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import { renderCollection } from "@dashboard/misc";
import { TableBody, TableCell, TableHead } from "@material-ui/core";
import { Button, Skeleton, Text } from "@saleor/macaw-ui-next";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { lotMessages, sectionMessages, sourceMessages } from "../../messages";
import { atticIntakePath, atticLotUrl } from "../../urls";
import { formatCost } from "../../utils";
import { AtticInventoryNav } from "../AtticInventoryNav/AtticInventoryNav";

const numberOfColumns = 6;

export interface AtticLotListPageProps {
  /** undefined while loading. */
  lots: AtticLotRowFragment[] | undefined;
  loadFailed: boolean;
}

export const AtticLotListPage = ({ lots, loadFailed }: AtticLotListPageProps) => {
  const intl = useIntl();
  const navigate = useNavigator();

  return (
    <ListPageLayout>
      <TopNav
        title={intl.formatMessage(sectionMessages.lots)}
        subtitle={
          <Text size={2} color="default2">
            {intl.formatMessage(lotMessages.subtitle)}
          </Text>
        }
      >
        <Button
          variant="primary"
          onClick={() => navigate(atticIntakePath)}
          data-test-id="attic-lot-intake"
        >
          {intl.formatMessage(sectionMessages.intake)}
        </Button>
      </TopNav>
      <AtticInventoryNav current="lots" />
      <DashboardCard data-test-id="attic-lot-list">
        <DashboardCard.Content paddingX={0}>
          <ResponsiveTable footer={<TablePaginationWithContext component="div" />}>
            <TableHead>
              <TableRowLink>
                <TableCell>{intl.formatMessage(lotMessages.reference)}</TableCell>
                <TableCell>{intl.formatMessage(lotMessages.source)}</TableCell>
                <TableCell>{intl.formatMessage(lotMessages.supplier)}</TableCell>
                <TableCell>{intl.formatMessage(lotMessages.received)}</TableCell>
                <TableCell align="right">{intl.formatMessage(lotMessages.onHand)}</TableCell>
                <TableCell align="right">{intl.formatMessage(lotMessages.cost)}</TableCell>
              </TableRowLink>
            </TableHead>
            <TableBody>
              {loadFailed ? (
                <TableRowLink data-test-id="attic-lot-list-failed">
                  <TableCell colSpan={numberOfColumns}>
                    <Text size={3} color="critical1">
                      {intl.formatMessage(sectionMessages.loadFailed)}
                    </Text>
                  </TableCell>
                </TableRowLink>
              ) : (
                renderCollection(
                  lots,
                  row => (
                    <TableRowLink
                      key={row ? row.id : "skeleton"}
                      hover={!!row}
                      onClick={row ? () => navigate(atticLotUrl(row.id)) : undefined}
                      data-test-id="attic-lot-row"
                    >
                      <TableCell>
                        {row ? (
                          <Link to={atticLotUrl(row.id)} onClick={event => event.stopPropagation()}>
                            <Text size={3} fontWeight="medium">
                              {row.reference || intl.formatMessage(lotMessages.untitled)}
                            </Text>
                          </Link>
                        ) : (
                          <Skeleton />
                        )}
                      </TableCell>
                      <TableCell>
                        {row ? (
                          <Text size={3}>{intl.formatMessage(sourceMessages[row.source])}</Text>
                        ) : (
                          <Skeleton />
                        )}
                      </TableCell>
                      <TableCell>
                        {row ? <Text size={3}>{row.supplier}</Text> : <Skeleton />}
                      </TableCell>
                      <TableCell>
                        {row ? (
                          <Text size={3}>
                            <DayLabel date={row.receivedAt} plain />
                          </Text>
                        ) : (
                          <Skeleton />
                        )}
                      </TableCell>
                      <TableCell align="right" data-test-id="attic-lot-units">
                        {row ? <Text size={3}>{row.unitCount}</Text> : <Skeleton />}
                      </TableCell>
                      <TableCell align="right" data-test-id="attic-lot-cost">
                        {row ? (
                          <Text size={3}>{formatCost(row.totalCost, row.currency, intl)}</Text>
                        ) : (
                          <Skeleton />
                        )}
                      </TableCell>
                    </TableRowLink>
                  ),
                  () => (
                    <TableRowLink data-test-id="attic-lot-list-empty">
                      <TableCell colSpan={numberOfColumns}>
                        <Text size={3} color="default2">
                          {intl.formatMessage(lotMessages.empty)}
                        </Text>
                      </TableCell>
                    </TableRowLink>
                  ),
                )
              )}
            </TableBody>
          </ResponsiveTable>
        </DashboardCard.Content>
      </DashboardCard>
    </ListPageLayout>
  );
};

export default AtticLotListPage;
